import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Smartphone,
  Share,
  Plus,
  MoreVertical,
  Download,
  CheckCircle2,
  Apple,
  Info,
} from 'lucide-react';
import { useInstallStore, detectPlatform, isStandaloneMode } from '@/lib/install';
import { toast } from 'sonner';

/**
 * Detailed multi-platform "Aggiungi alla home" instructions dialog.
 * Auto-detects the current device and pre-selects the right tab.
 */
export const InstallAppDialog = () => {
  const { open, closeDialog, deferredPrompt, clearDeferredPrompt } = useInstallStore();
  const detected = detectPlatform();
  const [tab, setTab] = useState(detected);

  useEffect(() => {
    if (open) setTab(detected);
  }, [open, detected]);

  const alreadyInstalled = isStandaloneMode();

  const tryNativePrompt = async () => {
    if (!deferredPrompt) {
      toast.info('Segui i passaggi qui sotto per aggiungere l\'app alla home.');
      return;
    }
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        toast.success('App installata! La trovi nella home del tuo dispositivo.');
        closeDialog();
      }
      clearDeferredPrompt();
    } catch (e) {
      // fall through to manual instructions
    }
  };

  const TabButton = ({ value, label }) => (
    <button
      type="button"
      onClick={() => setTab(value)}
      data-testid={`install-tab-${value}`}
      className={`flex-1 sm:flex-none px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg transition ${
        tab === value
          ? 'bg-[--brand-primary] text-white shadow-[var(--shadow-sm)]'
          : 'bg-[--brand-surface-2] text-[--brand-muted] hover:bg-[--brand-border]'
      }`}
    >
      {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeDialog()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="install-app-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Smartphone size={20} className="text-[--brand-primary]" />
            Scarica l&apos;app MōMA • Pokè
          </DialogTitle>
          <DialogDescription>
            Aggiungi MōMA • Pokè alla home del tuo cellulare per ordinare più velocemente,
            ricevere aggiornamenti e usarla come una vera app — senza passare dagli store.
          </DialogDescription>
        </DialogHeader>

        {alreadyInstalled && (
          <div className="flex items-center gap-2 rounded-xl bg-[#E8F5EE] text-[#1F6F4B] border border-[#B7DEC5] px-3 py-2 text-sm">
            <CheckCircle2 size={16} /> App già installata sul tuo dispositivo.
          </div>
        )}

        {/* Native install button (Chrome/Edge desktop + Chrome Android) */}
        {deferredPrompt && !alreadyInstalled && (
          <div className="rounded-xl bg-[--brand-primary]/5 border border-[--brand-primary]/20 p-3">
            <div className="text-sm font-semibold text-[--brand-primary] mb-1">
              Installazione in un click
            </div>
            <div className="text-xs text-[--brand-muted] mb-2">
              Il tuo browser supporta l&apos;installazione automatica.
            </div>
            <Button
              onClick={tryNativePrompt}
              className="w-full rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover] text-white"
              data-testid="install-native-prompt-button"
            >
              <Download size={16} className="mr-2" /> Installa ora
            </Button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap">
          <TabButton value="ios-safari" label="iPhone / iPad" />
          <TabButton value="android-chrome" label="Android" />
          <TabButton value="desktop" label="Computer" />
        </div>

        {/* Content per platform */}
        <div className="rounded-2xl border border-[--brand-border] bg-[--brand-surface-2] p-4">
          {(tab === 'ios-safari' || tab === 'ios-other') && <IOSSteps isChromeIOS={tab === 'ios-other'} />}
          {(tab === 'android-chrome' || tab === 'android-samsung' || tab === 'android-other') && (
            <AndroidSteps browser={tab} />
          )}
          {tab === 'desktop' && <DesktopSteps />}
        </div>

        <div className="text-xs text-[--brand-muted] flex items-start gap-2">
          <Info size={14} className="shrink-0 mt-0.5" />
          <span>
            Una volta aggiunta alla home, MōMA • Pokè funzionerà come una vera app:
            icona propria, apertura a schermo intero e login memorizzato.
          </span>
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            className="rounded-xl w-full sm:w-auto"
            onClick={closeDialog}
            data-testid="install-dialog-close"
          >
            Ho capito
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Step blocks ---------- */

const Step = ({ n, children }) => (
  <li className="flex gap-3 py-2">
    <span className="shrink-0 w-7 h-7 rounded-full bg-[--brand-primary] text-white text-sm font-bold flex items-center justify-center">
      {n}
    </span>
    <div className="pt-0.5 text-sm leading-relaxed text-[--brand-text]">{children}</div>
  </li>
);

const IOSSteps = ({ isChromeIOS }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <Apple size={18} />
      <div className="font-semibold">iPhone / iPad</div>
      <Badge className="ml-auto rounded-full bg-white text-[--brand-primary] border border-[--brand-border]">
        iOS 14+
      </Badge>
    </div>
    {isChromeIOS && (
      <div className="rounded-lg bg-white border border-[--brand-border] p-2 mb-3 text-xs text-[--brand-muted]">
        Su iPhone puoi installare l&apos;app <b>solo da Safari</b>. Se stai usando Chrome
        o un altro browser, aprilo prima con Safari.
      </div>
    )}
    <ol className="space-y-1">
      <Step n={1}>
        Apri questo sito con <b>Safari</b> sul tuo iPhone o iPad.
      </Step>
      <Step n={2}>
        Tocca l&apos;icona <b>Condividi</b>
        <span className="inline-flex items-center justify-center align-middle mx-1 w-6 h-6 rounded-md bg-white border border-[--brand-border]">
          <Share size={13} />
        </span>
        nella barra in basso (o in alto a destra su iPad).
      </Step>
      <Step n={3}>
        Nel menu, scorri e tocca <b>&quot;Aggiungi alla schermata Home&quot;</b>
        <span className="inline-flex items-center justify-center align-middle mx-1 w-6 h-6 rounded-md bg-white border border-[--brand-border]">
          <Plus size={13} />
        </span>
      </Step>
      <Step n={4}>
        Conferma toccando <b>Aggiungi</b> in alto a destra.
      </Step>
      <Step n={5}>
        L&apos;icona di MōMA • Pokè apparirà nella home. Toccala per aprire l&apos;app.
      </Step>
    </ol>
  </div>
);

const AndroidSteps = ({ browser }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <Smartphone size={18} />
      <div className="font-semibold">Android</div>
      <Badge className="ml-auto rounded-full bg-white text-[--brand-primary] border border-[--brand-border]">
        {browser === 'android-samsung' ? 'Samsung Internet' : 'Chrome'}
      </Badge>
    </div>
    <ol className="space-y-1">
      <Step n={1}>
        Apri questo sito con <b>{browser === 'android-samsung' ? 'Samsung Internet' : 'Chrome'}</b> sul tuo cellulare.
      </Step>
      <Step n={2}>
        In molti casi appare in basso il banner{' '}
        <b>&quot;Aggiungi MōMA • Pokè a Home&quot;</b>: toccalo e conferma. Se non lo vedi, prosegui al passo successivo.
      </Step>
      <Step n={3}>
        Tocca il menu{' '}
        <span className="inline-flex items-center justify-center align-middle mx-1 w-6 h-6 rounded-md bg-white border border-[--brand-border]">
          <MoreVertical size={13} />
        </span>{' '}
        in alto a destra.
      </Step>
      <Step n={4}>
        Scegli <b>&quot;Installa app&quot;</b> oppure <b>&quot;Aggiungi a schermata Home&quot;</b>.
      </Step>
      <Step n={5}>
        Conferma con <b>Installa</b>. Trovi l&apos;icona nella home o nel drawer delle app.
      </Step>
    </ol>
  </div>
);

const DesktopSteps = () => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <Download size={18} />
      <div className="font-semibold">Computer (Chrome / Edge)</div>
    </div>
    <ol className="space-y-1">
      <Step n={1}>
        Apri il sito con <b>Google Chrome</b> o <b>Microsoft Edge</b>.
      </Step>
      <Step n={2}>
        Nella barra degli indirizzi, sul lato destro, appare un&apos;icona{' '}
        <span className="inline-flex items-center justify-center align-middle mx-1 w-6 h-6 rounded-md bg-white border border-[--brand-border]">
          <Download size={13} />
        </span>{' '}
        di installazione. Clicca sopra.
      </Step>
      <Step n={3}>
        Conferma cliccando <b>Installa</b> nella finestra di dialogo.
      </Step>
      <Step n={4}>
        L&apos;app si aprirà in una finestra dedicata e apparirà nel menu Start / Applicazioni.
      </Step>
    </ol>
  </div>
);
